<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import ConversationApi from 'dashboard/api/conversations';

// 折叠项展开时每 10s 拉一次，实时展示访客浏览轨迹（收起时组件卸载，自动停）
const POLL_INTERVAL = 10000;

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const trail = ref([]);
const visitCount = ref(0);
const pageDepth = ref(0);
const isLoading = ref(false);
const hasError = ref(false);
let pollTimer = null;

// 按时间升序的访问记录，补齐相邻停留时长
const timeline = computed(() => {
  const sorted = [...trail.value].sort((a, b) => a.ts - b.ts);
  return sorted.map((entry, index) => {
    const next = sorted[index + 1];
    const dwellMs = next ? next.ts - entry.ts : null;
    return { ...entry, dwell: dwellMs };
  });
});

const isEmpty = computed(
  () => !isLoading.value && !hasError.value && trail.value.length === 0
);

const formatTime = ts => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour12: false });
};

// 将毫秒差格式化为「X分Y秒」/「Y秒」
const formatDuration = ms => {
  if (ms == null) return '';
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes} 分 ${seconds} 秒`;
  }
  return `${seconds} 秒`;
};

// silent=true 为轮询调用，不切换 loading（避免每 10s 闪烁）
const fetchTrail = async (silent = false) => {
  if (!props.conversationId) return;
  if (!silent) isLoading.value = true;
  try {
    const { data } = await ConversationApi.getVisitorTrail(
      props.conversationId
    );
    trail.value = data.trail || [];
    visitCount.value = data.visit_count || 0;
    pageDepth.value = data.page_depth || 0;
    hasError.value = false;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.conversationId, () => fetchTrail());

onMounted(() => {
  fetchTrail();
  pollTimer = setInterval(() => fetchTrail(true), POLL_INTERVAL);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});
</script>

<template>
  <div class="flex flex-col gap-3 py-2 m-0">
    <!-- 顶部统计：浏览深度 / 来访次数 -->
    <div
      v-if="!hasError"
      class="grid grid-cols-2 gap-2"
    >
      <div
        class="flex flex-col items-center gap-0.5 py-3 px-2 rounded-lg bg-n-slate-2 border border-solid border-n-weak"
      >
        <span class="text-2xl font-bold leading-tight text-n-slate-12">
          {{ pageDepth }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.PAGE_DEPTH') }}
        </span>
      </div>
      <div
        class="flex flex-col items-center gap-0.5 py-3 px-2 rounded-lg bg-n-slate-2 border border-solid border-n-weak"
      >
        <span class="text-2xl font-bold leading-tight text-n-slate-12">
          {{ visitCount }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.VISIT_COUNT') }}
        </span>
      </div>
    </div>

    <p
      v-if="isLoading"
      class="text-sm text-center text-n-slate-10 py-3 m-0"
    >
      {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.LOADING') }}
    </p>
    <p
      v-else-if="hasError"
      class="text-sm text-center text-n-ruby-10 py-3 m-0"
    >
      {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.ERROR') }}
    </p>
    <p
      v-else-if="isEmpty"
      class="text-sm text-center text-n-slate-10 py-3 m-0"
    >
      {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.EMPTY') }}
    </p>

    <ol v-else class="flex flex-col gap-0.5 list-none m-0 p-0">
      <template v-for="(entry, index) in timeline" :key="index">
        <li class="flex flex-wrap items-baseline gap-1.5">
          <span class="text-xs text-n-slate-9 tabular-nums">
            {{ formatTime(entry.ts) }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.ENTERED') }}
          </span>
          <a
            v-if="entry.url"
            :href="entry.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-medium text-n-brand hover:underline break-all"
          >
            {{
              entry.title || $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.UNTITLED')
            }}
          </a>
          <span v-else class="text-sm font-medium text-n-slate-12 break-all">
            {{
              entry.title || $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.UNTITLED')
            }}
          </span>
        </li>
        <li
          v-if="entry.dwell != null"
          class="ml-3 pl-2.5 mb-1.5 text-xs text-n-slate-9 border-l-2 border-solid border-n-weak"
        >
          {{
            $t('CONVERSATION_SIDEBAR.VISITOR_TRAIL.STAYED', {
              duration: formatDuration(entry.dwell),
            })
          }}
        </li>
      </template>
    </ol>
  </div>
</template>
